/**
 * BuilderPage - Worksheet Builder Page
 *
 * Features:
 * - Drag-and-drop worksheet creation
 * - Component palette with all question types
 * - Property editor for selected components
 * - Preview mode with answer key toggle
 * - Auto-save and manual save
 * - Mobile: Shows "Use desktop" message
 * - AbortController for cleanup
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { WorksheetBuilder } from "@/components/builder";
import type { WorksheetTemplate } from "@/components/builder/types";
import type { CurriculumOutcome } from "@/components/planner/types";
import {
  worksheetBuilderService,
  type SaveWorksheetRequest,
} from "@/services/worksheetBuilder.service";
import { toast } from "sonner";
import { Monitor, Smartphone, AlertCircle, ArrowLeft, Plus } from "lucide-react";

// Import curriculum data
import curriculumData from "../../../curriculum/parsed_full.json";

// ============================================================================
// Types
// ============================================================================

interface BuilderState {
  worksheet: WorksheetTemplate | null;
  isNew: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error && typeof error === "object") {
    const err = error as { code?: string; name?: string };
    if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
      return true;
    }
  }
  return false;
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

// ============================================================================
// Mobile Message Component
// ============================================================================

function MobileMessage({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/50">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-4 rounded-full bg-amber-100">
            <Smartphone className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle>Desktop Required</CardTitle>
          <CardDescription>
            The worksheet builder requires a larger screen for the best
            experience.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
            <Monitor className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Use a desktop or laptop</p>
              <p className="text-muted-foreground">
                Open this page on a device with at least 1024px screen width.
              </p>
            </div>
          </div>

          <Button onClick={onBack} variant="outline" className="w-full gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Worksheets
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function BuilderPage() {
  const navigate = useNavigate();
  const { worksheetId } = useParams<{ worksheetId?: string }>();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Check if duplicating
  const duplicateId = searchParams.get("duplicate");

  // Curriculum outcomes
  const outcomes = useMemo<CurriculumOutcome[]>(
    () => curriculumData as CurriculumOutcome[],
    []
  );

  // State
  const [state, setState] = useState<BuilderState>({
    worksheet: null,
    isNew: !worksheetId && !duplicateId,
  });
  const [loading, setLoading] = useState(!state.isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Load existing worksheet or duplicate
  useEffect(() => {
    const idToLoad = worksheetId || duplicateId;

    if (!idToLoad) {
      // New worksheet - nothing to load
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const loadWorksheet = async () => {
      try {
        setLoading(true);
        setError(null);

        if (duplicateId) {
          // Duplicate: load and create copy
          const duplicated = await worksheetBuilderService.duplicateWorksheet(
            duplicateId,
            controller.signal
          );

          if (controller.signal.aborted) return;

          setState({
            worksheet: duplicated,
            isNew: false, // It's been created on server
          });

          // Navigate to the new worksheet's URL
          navigate(`/builder/${duplicated.id}`, { replace: true });
          toast.success("Worksheet duplicated");
        } else if (worksheetId) {
          // Edit existing
          const worksheet = await worksheetBuilderService.getWorksheet(
            worksheetId,
            controller.signal
          );

          if (controller.signal.aborted) return;

          setState({
            worksheet,
            isNew: false,
          });
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to load worksheet:", err);
          const message =
            err instanceof Error ? err.message : "Failed to load worksheet";
          setError(message);
          toast.error("Failed to load worksheet");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadWorksheet();

    return () => controller.abort();
  }, [worksheetId, duplicateId, navigate]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate("/worksheets");
  }, [navigate]);

  // Handle save
  const handleSave = useCallback(
    async (worksheet: WorksheetTemplate) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setSaving(true);

        const saveData: SaveWorksheetRequest = {
          title: worksheet.title,
          description: worksheet.description,
          grade: worksheet.grade,
          subject: worksheet.subject,
          outcomeIds: worksheet.outcomeIds,
          difficulty: worksheet.difficulty,
          estimatedMinutes: worksheet.estimatedMinutes,
          usageFrequency: worksheet.usageFrequency,
          teacherNotes: worksheet.teacherNotes,
          components: worksheet.components,
          layout: worksheet.layout,
          gradingConfig: worksheet.gradingConfig,
          status: worksheet.status,
        };

        let saved: WorksheetTemplate;

        if (state.isNew || !worksheet.id) {
          // Create new
          saved = await worksheetBuilderService.createWorksheet(
            saveData,
            controller.signal
          );

          // Navigate to the new URL
          navigate(`/builder/${saved.id}`, { replace: true });
        } else {
          // Update existing
          saved = await worksheetBuilderService.updateWorksheet(
            worksheet.id,
            saveData,
            controller.signal
          );
        }

        if (controller.signal.aborted) return;

        setState({
          worksheet: saved,
          isNew: false,
        });
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to save worksheet:", err);
          throw err; // Re-throw to let builder handle error display
        }
      } finally {
        if (!controller.signal.aborted) {
          setSaving(false);
        }
      }
    },
    [state.isNew, navigate]
  );

  // Handle auto-save (debounced, for drafts only)
  const handleAutoSave = useCallback(
    async (worksheet: WorksheetTemplate) => {
      // Only auto-save if already saved to server and is a draft
      if (!worksheet.id || worksheet.status !== "draft") return;

      try {
        await worksheetBuilderService.autoSave(worksheet.id, {
          title: worksheet.title,
          components: worksheet.components,
          layout: worksheet.layout,
        });
      } catch (err) {
        // Silently fail auto-save - user can manually save
        console.warn("Auto-save failed:", err);
      }
    },
    []
  );

  // Show mobile message
  if (isMobile) {
    return <MobileMessage onBack={handleBack} />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex flex-col">
        <div className="h-14 border-b bg-card flex items-center px-4 gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-9 w-64" />
          <div className="flex-1" />
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="flex-1 flex">
          <Skeleton className="w-64 h-full" />
          <div className="flex-1 p-6">
            <Skeleton className="w-full h-full" />
          </div>
          <Skeleton className="w-80 h-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="mx-auto mb-4 p-4 rounded-full bg-red-100">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-center">Error Loading Worksheet</CardTitle>
            <CardDescription className="text-center">{error}</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="flex-1 gap-2"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <WorksheetBuilder
      initialWorksheet={state.worksheet || undefined}
      outcomes={outcomes}
      onSave={handleSave}
      onBack={handleBack}
      isSaving={saving}
      onAutoSave={handleAutoSave}
    />
  );
}
